import { ArrowRight, Download, FileText, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { formatDocTitle, parseDocChronoTimestamp } from '../lib/document-display';
import { pb } from '../lib/pb';
import { usePageMeta } from '../lib/usePageMeta';

interface PublicDocument {
  id: string;
  title: string;
  category: string;
  is_approved: boolean;
  file: string;
  processed_pdf: string;
  original_filename: string;
  created: string;
  collectionId: string;
}

/**
 * Section ordering + category labels on the public Documents page.
 * Categories from the PB schema (8 enum values) collapse into these
 * presentation sections.
 */
const SECTIONS: {
  id: string;
  eyebrow: string;
  title: string;
  lead: string;
  categories: string[];
}[] = [
  {
    id: 'budget',
    eyebrow: 'Annual Budget',
    title: 'Approved operating budgets',
    lead: 'The current fiscal-year budget, approved by the board. Older budgets retained in the back-office archive and available on request.',
    categories: ['financials'],
  },
  {
    id: 'minutes',
    eyebrow: 'Meeting Minutes and Reports',
    title: 'Board and member meetings',
    lead: 'The last twelve months of board and annual member meeting minutes, plus the President’s reports. Older minutes are archived for seven years per Florida Statute 718.111(12)(b)11 and are available to owners on request.',
    categories: ['minutes', 'president-report'],
  },
  {
    id: 'governing',
    eyebrow: 'Governing Documents',
    title: 'Declaration, Bylaws, Articles, amendments, and structural reports',
    lead: 'The condominium’s governing documents (Declaration of Condominium, Bylaws, Articles of Incorporation, recorded amendments through January 2021) and the structural compliance reports filed in support of Florida Statutes 553.899 (Milestone Inspection) and 718.112(2)(g) (Structural Integrity Reserve Study).',
    categories: ['declaration', 'legal'],
  },
  {
    id: 'other',
    eyebrow: 'Other Records',
    title: 'Additional public records',
    lead: '',
    categories: ['misc'],
  },
];

const MINUTES_CUTOFF_DAYS = 365;

function withinLast12Months(iso: string): boolean {
  const created = new Date(iso).getTime();
  const cutoff = Date.now() - MINUTES_CUTOFF_DAYS * 24 * 60 * 60 * 1000;
  return created >= cutoff;
}

export function Documents(): JSX.Element {
  const [docs, setDocs] = useState<PublicDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  usePageMeta();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await pb.collection('documents').getList<PublicDocument>(1, 200, {
          filter: 'is_owner_only = false && is_admin_only = false && status = "ready"',
          sort: '-created',
          fields:
            'id,title,category,is_approved,file,processed_pdf,original_filename,created,collectionId',
        });
        if (!cancelled) setDocs(result.items);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Could not load documents. Refresh in a moment.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function fileUrl(doc: PublicDocument): string {
    return pb.files.getURL(doc, doc.processed_pdf || doc.file, { download: true });
  }

  function visibleForSection(
    section: (typeof SECTIONS)[number],
    all: PublicDocument[],
  ): PublicDocument[] {
    let matched = all.filter((d) => section.categories.includes(d.category));
    if (section.id === 'minutes') {
      matched = matched.filter((d) => withinLast12Months(d.created));
    }
    // Sort every section chronologically by the date in the title (newest first)
    // so meetings, budgets, and reports all land top-down by what they're
    // about, not by when they happened to get uploaded.
    return matched.sort(
      (a, b) => parseDocChronoTimestamp(b.title) - parseDocChronoTimestamp(a.title),
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Records"
        title="Documents"
        lead="Association records, organized by category. Public records live here. Owner-restricted records are inside the Owner Portal."
        photo={{
          src1600: '/photos/image10-1600.webp',
          src800: '/photos/image10-800.webp',
          alt: "Aerial of Sunset Harbor's east elevation with the Banana River filling the background, the mainland visible on the horizon, and palm-lined grounds in the foreground.",
          objectPosition: 'center 50%',
        }}
      />

      <section className="section bg-brand-white">
        <Container>
          {/* Nav rail */}
          <nav aria-label="Documents on this page" className="mb-12">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
              On this page
            </p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {SECTIONS.filter((s) =>
                (docs ?? []).some((d) => s.categories.includes(d.category)),
              ).map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 transition-colors hover:decoration-brand-yellow-deep"
                  >
                    {s.eyebrow}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#insurance"
                  className="text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 transition-colors hover:decoration-brand-yellow-deep"
                >
                  Insurance and sensitive records
                </a>
              </li>
            </ul>
          </nav>

          {error ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          ) : docs === null ? (
            <p className="text-sm text-brand-body-medium">Loading...</p>
          ) : (
            <div className="space-y-16">
              {SECTIONS.map((section) => {
                const items = visibleForSection(section, docs);
                if (items.length === 0) return null;
                return (
                  <div key={section.id} id={section.id} className="scroll-mt-24">
                    <div className="mb-8 max-w-2xl">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                        {section.eyebrow}
                      </p>
                      <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
                        {section.title}
                      </h2>
                      {section.lead ? (
                        <p className="mt-4 text-base leading-relaxed text-brand-body-dark">
                          {section.lead}
                        </p>
                      ) : null}
                    </div>

                    <ul className="space-y-3">
                      {items.map((doc) => (
                        <li
                          key={doc.id}
                          className="flex flex-col gap-4 rounded-lg border border-brand-surface bg-brand-white p-5 shadow-card transition-shadow hover:shadow-card-hover md:flex-row md:items-center"
                        >
                          <div className="flex items-start gap-4 md:flex-1">
                            <span
                              className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-surface text-brand-charcoal"
                              aria-hidden="true"
                            >
                              <FileText size={20} strokeWidth={1.75} />
                            </span>
                            <div>
                              <p className="text-base font-bold text-brand-charcoal">
                                {formatDocTitle(doc)}
                              </p>
                              <p className="mt-1 text-sm text-brand-body-medium">PDF</p>
                            </div>
                          </div>
                          <a
                            href={fileUrl(doc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={`${doc.title}.pdf`}
                            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 md:w-auto"
                            aria-label={`Download ${doc.title}, PDF`}
                          >
                            <Download size={16} aria-hidden="true" />
                            Download
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              {/* Insurance Certificate callout: handled instantly by iCerts, not by the board */}
              <div
                id="insurance"
                className="scroll-mt-24 rounded-lg border border-brand-surface bg-brand-surface p-8 md:p-10"
              >
                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-charcoal text-brand-yellow"
                    aria-hidden="true"
                  >
                    <ShieldCheck size={20} strokeWidth={2} />
                  </span>
                  <div className="max-w-2xl">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
                      Certificate of Insurance
                    </p>
                    <h2 className="text-2xl font-bold tracking-heading-tight text-brand-charcoal md:text-3xl">
                      Requesting a certificate
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-brand-body-dark">
                      Certificates of Insurance for unit sales, refinances, and lender requests are
                      issued through iCerts, not by the board. The steps depend on your situation.
                      Please do not file a records request for a COI.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Button to="/insurance-certificate" size="md">
                        How to request one
                        <ArrowRight size={16} aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}
